import {
  Add as AddIcon,
  DescriptionOutlined as DescriptionOutlinedIcon,
} from "@/components/icons";
import * as React from "react";
import {
  Avatar,
  Badge,
  Box,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { useSessions } from "@/api/hooks";
import type { SessionSummary } from "@/api/types";
import { brand } from "@/theme";
import { displaySessionTitle } from "@/features/sessions/sessionTitle";

/** Vertical avatar rail shown when the sidebar is collapsed.
 *
 * Each session renders as a circular avatar (initials, colour-hashed).
 * Selected gets an accent ring. A ``+`` at top spawns a new session.
 * Hover reveals the session title + timestamp in a tooltip; click
 * navigates. Sessions with documents get a tiny badge.
 */
export function CollapsedSessionRail(): React.JSX.Element {
  const { t } = useTranslation();
  const { data } = useSessions();
  const navigate = useNavigate();
  const location = useLocation();
  const all = data ?? [];
  // Cap so the rail doesn't overflow the viewport. A '+N more' chip
  // below the rail signals the overflow so users know to expand the
  // sidebar instead of wondering where the extra sessions went.
  const RAIL_CAP = 30;
  const rows = all.slice(0, RAIL_CAP);
  const hiddenCount = Math.max(0, all.length - rows.length);

  return (
    <Stack
      sx={{
        alignItems: "center",
        py: 1,
        gap: 1,
        flex: 1,
        overflowY: "auto",
        overflowX: "hidden",
      }}
    >
      <Tooltip title={t("common.new_session")} placement="right">
        <IconButton
          size="small"
          color="primary"
          component={Link}
          to="/app/sessions"
          sx={{
            bgcolor: "primary.main",
            color: "primary.contrastText",
            "&:hover": { bgcolor: "primary.dark" },
            width: 32,
            height: 32,
          }}
        >
          <AddIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Divider flexItem sx={{ mx: 1 }} />
      {rows.map((s) => {
        const selected = location.pathname.endsWith(`/sessions/${s.id}`);
        return (
          <Tooltip
            key={s.id}
            placement="right"
            title={<SessionTooltip session={s} />}
          >
            <Box
              onClick={() => navigate(`/app/sessions/${s.id}`)}
              sx={{
                cursor: "pointer",
                position: "relative",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Badge
                overlap="circular"
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                badgeContent={
                  s.document_count > 0 ? (
                    <DescriptionOutlinedIcon sx={{ fontSize: 10 }} />
                  ) : null
                }
                slotProps={{
                  badge: {
                    sx: {
                      bgcolor: "background.paper",
                      color: "primary.main",
                      border: 1,
                      borderColor: "primary.main",
                      height: 16,
                      minWidth: 16,
                      padding: 0,
                    },
                  },
                }}
              >
                <Avatar
                  sx={{
                    width: 32,
                    height: 32,
                    fontSize: 13,
                    fontWeight: 600,
                    bgcolor: avatarColor(s.id),
                    outline: selected
                      ? `2px solid ${brand.text}`
                      : undefined,
                    outlineOffset: 2,
                  }}
                >
                  {initials(displaySessionTitle(t, s.title))}
                </Avatar>
              </Badge>
            </Box>
          </Tooltip>
        );
      })}
      {hiddenCount > 0 && (
        <Tooltip
          title={t("sidebar.rail_more", {
            count: hiddenCount,
            defaultValue:
              "{{count}} more sessions — expand the sidebar to see them",
          })}
          placement="right"
        >
          <Avatar
            sx={{
              width: 32,
              height: 32,
              fontSize: 11,
              bgcolor: brand.ljusgra,
              color: "text.secondary",
              border: 1,
              borderStyle: "dashed",
              borderColor: "divider",
            }}
          >
            +{hiddenCount}
          </Avatar>
        </Tooltip>
      )}
    </Stack>
  );
}

function SessionTooltip({ session }: { session: SessionSummary }): React.JSX.Element {
  const { t } = useTranslation();
  const intentLabel = t(`intents.${session.intent}`, {
    defaultValue: session.intent,
  });
  return (
    <Box sx={{ py: 0.5 }}>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {displaySessionTitle(t, session.title)}
      </Typography>
      {session.repository && (
        <Typography variant="caption" sx={{ display: "block", opacity: 0.8 }}>
          {session.repository.path_with_namespace}
          {session.branch_name ? ` · ${session.branch_name}` : ""}
        </Typography>
      )}
      <Typography variant="caption" sx={{ display: "block", opacity: 0.7 }}>
        {intentLabel}
        {session.document_count > 0
          ? ` · ${t("sidebar.rail_docs", {
              count: session.document_count,
              defaultValue: "{{count}} docs",
            })}`
          : ""}
      </Typography>
    </Box>
  );
}

function initials(title: string): string {
  const words = title.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return "?";
  const first = words[0] ?? "";
  if (words.length === 1) return first.slice(0, 2).toUpperCase() || "?";
  const last = words[words.length - 1] ?? first;
  const a = first[0] ?? "";
  const b = last[0] ?? "";
  return (a + b).toUpperCase() || "?";
}

// Stable hash → HSL colour so the avatar identity stays put across
// reloads. Same input always produces the same hue.
function avatarColor(seed: string): string {
  let h = 0;
  for (let i = 0; i < seed.length; i += 1) {
    h = (h * 31 + seed.charCodeAt(i)) | 0;
  }
  const hue = Math.abs(h) % 360;
  return `hsl(${hue} 45% 42%)`;
}
